import React from 'react'
import styled from 'styled-components'
import breakpoint from '@jarvis.oddle/core/tools/styled-components-breakpoint'
import {
  dropShadows,
  dropShadowVariations,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import richTextHtmlParser from '@jarvis.oddle/core/components/TextEditor/parser'
import Form, { withFormContext } from '@jarvis.oddle/core/components/Form'
import {
  getEmailError,
  getFirstNameError,
  getLastNameError
} from '@jarvis.oddle/core/tools/validation'
import { H3, P } from '@jarvis.oddle/core/components/Typo'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'

const FormContainer = styled(Div).attrs(() => ({
  width: '100%',
  marginTop: marginSizes.small
}))`
  ${breakpoint('tablet')`
    max-width: 300px;
  `}
`

const InputContainer = styled(Div).attrs(() => ({
  marginBottom: marginSizes.small
}))`
  width: 100%;
`

const StyledButton = styled.button`
  padding: ${padding[paddingSizes.small]};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colorBodyFont};
  outline: none;
  border: none;
  cursor: pointer;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  box-shadow: ${dropShadows[dropShadowVariations.heavy]};
`

const StyledText = styled(P)`
  color: ${(props) => props.theme.colorBackgroundColor};
`

const StyledFormSubmitButton = withFormContext(
  ({ form, disabled, children }) => (
    <StyledButton
      onClick={form.handleSubmit}
      disabled={disabled || form.isSubmitting}
    >
      {children}
    </StyledButton>
  )
)

const Newsletter = ({
  newsletterSignupAction,
  previewMode,
  settings
}: {
  newsletterSignupAction: (params) => Promise<{
    meta: {
      requestStatus: string
    }
  }>
  previewMode: boolean
  settings: {
    bodyText: string
    headingsText: string
    requestSubscriberName: boolean
    successMessageText: string
  }
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const [submitSucceed, setSubmitSucceed] = React.useState(false)

  const onSubmit = async (values, actions) => {
    const { email, firstName, lastName } = values
    const { setFieldError, setSubmitting } = actions
    if (previewMode) {
      setSubmitSucceed(true)
      setSubmitting(false)
      return
    }
    const response = await newsletterSignupAction({
      email,
      firstName,
      lastName
    })
    if (response.meta.requestStatus === 'fulfilled') {
      setSubmitSucceed(true)
    } else {
      setFieldError(
        'email',
        t('Looks like something went wrong, please try again later.')
      )
      setSubmitting(false)
    }
  }

  const onValidate = (values) => {
    const { email, firstName, lastName } = values
    const { requestSubscriberName } = settings
    const errors = {} as {
      email?: string
      firstName?: string
      lastName?: string
    }

    const emailError = getEmailError(t, email)
    if (emailError) errors.email = emailError

    if (requestSubscriberName) {
      const firstNameError = getFirstNameError(t, firstName)
      const lastNameError = getLastNameError(t, lastName)
      if (firstNameError) errors.firstName = firstNameError
      if (lastNameError) errors.lastName = lastNameError
    }

    return errors
  }

  const { requestSubscriberName, bodyText, successMessageText } = settings
  const bodyTextElement = richTextHtmlParser(bodyText, { normalised: true })
  const successMessageTextElement = richTextHtmlParser(successMessageText, {
    normalised: true
  })
  return (
    <Div>
      <H3 normalised marginBottom={marginSizes.small} bold>
        {settings.headingsText}
      </H3>
      {submitSucceed ? (
        successMessageTextElement
      ) : (
        <React.Fragment>
          {bodyTextElement}
          <FormContainer>
            <Form
              initialValues={{ firstName: '', lastName: '', email: '' }}
              onSubmit={onSubmit}
              onValidate={onValidate}
            >
              {requestSubscriberName && (
                <React.Fragment>
                  <Form.Input
                    Container={InputContainer}
                    name='firstName'
                    label={t('First Name')}
                  />
                  <Form.Input
                    Container={InputContainer}
                    name='lastName'
                    label={t('Last Name')}
                  />
                </React.Fragment>
              )}
              <Flex>
                <Form.Input
                  Container={InputContainer}
                  name='email'
                  label={t('Email')}
                >
                  {({ inputElement }) => (
                    <React.Fragment>
                      {inputElement}
                      <StyledFormSubmitButton>
                        <StyledText bold normalised>
                          {t('Subscribe')}
                        </StyledText>
                      </StyledFormSubmitButton>
                    </React.Fragment>
                  )}
                </Form.Input>
              </Flex>
            </Form>
          </FormContainer>
        </React.Fragment>
      )}
    </Div>
  )
}

export default Newsletter
